import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { logEvent } from "@/lib/telemetry";

export const ASSISTANT_HISTORY_KEY = ["assistant", "history"];

export const assistantApi = {
  getHistory: () => api.get("/assistant/history").then((r) => r.data),

  send: async (content) => {
    // Phase 14 — fire-and-forget telemetry BEFORE the send so the event
    // fires even if the user cancels or the network hiccups mid-request.
    logEvent("robo_ball_query");
    const r = await api.post(
      "/assistant/message",
      { content },
      { validateStatus: (s) => s < 500 || s === 503 }
    );
    if (r.status === 503) {
      const err = new Error("assistant_unavailable");
      err.code = "assistant_unavailable";
      err.status = 503;
      throw err;
    }
    if (r.status >= 400) {
      const err = new Error("send_failed");
      err.status = r.status;
      err.data = r.data;
      throw err;
    }
    return r.data;
  },

  clear: () =>
    api.delete("/assistant/history", { validateStatus: (s) => s < 500 }).then((r) => r.data),
};

export function useAssistantHistory(enabled = true) {
  return useQuery({
    queryKey: ASSISTANT_HISTORY_KEY,
    queryFn: assistantApi.getHistory,
    enabled,
    staleTime: 0,
  });
}

export function useSendAssistantMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assistantApi.send,
    onMutate: async (content) => {
      // Optimistic append of user message
      await qc.cancelQueries({ queryKey: ASSISTANT_HISTORY_KEY });
      const prev = qc.getQueryData(ASSISTANT_HISTORY_KEY);
      const nowIso = new Date().toISOString();
      qc.setQueryData(ASSISTANT_HISTORY_KEY, (old) => {
        const msgs = (old?.messages || []).slice();
        msgs.push({ role: "user", content, created_at: nowIso });
        return { messages: msgs };
      });
      return { prev };
    },
    onSuccess: (data) => {
      qc.setQueryData(ASSISTANT_HISTORY_KEY, (old) => {
        const msgs = (old?.messages || []).slice();
        msgs.push(data.message);
        return { messages: msgs };
      });
    },
    onError: (_err, _content, ctx) => {
      // Rollback to last known good state if we have it
      if (ctx?.prev) qc.setQueryData(ASSISTANT_HISTORY_KEY, ctx.prev);
    },
  });
}

export function useClearAssistantHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assistantApi.clear,
    onSuccess: () => {
      qc.setQueryData(ASSISTANT_HISTORY_KEY, { messages: [] });
    },
  });
}
